import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, ArrowDown, TrendingUp, Users, Mail, Target, Palette, BarChart3, Star, Code, Bot } from 'lucide-react';
import { Button } from '../components/ui/button';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_d69e6772-669a-4c5a-9337-e48f09c8c595/artifacts/dosqwhmo_361589719_248985681240088_4755260591056983724_n.jpg";

const services = [
  {
    icon: Users,
    title: 'Social Media Management',
    description: 'Strategic content creation and community management across TikTok, Instagram, Facebook, LinkedIn, and more.',
  },
  {
    icon: TrendingUp,
    title: 'Affiliate Marketing',
    description: 'Scale your brand with vetted creator partnerships and optimized affiliate programs.',
  },
  {
    icon: Mail,
    title: 'Email Marketing',
    description: 'Engaging campaigns that nurture leads and drive conversions with personalized messaging.',
  },
  {
    icon: Target,
    title: 'Ads Management',
    description: 'Data-driven ad optimization that maximizes ROAS and minimizes wasted spend.',
  },
  {
    icon: Palette,
    title: 'Content Strategy',
    description: 'Compelling content that resonates with your audience and builds brand authority.',
  },
  {
    icon: BarChart3,
    title: 'Analytics & Reporting',
    description: 'Comprehensive KPI tracking and insights to inform strategic decisions.',
  },
  {
    icon: Code,
    title: 'Web Development',
    description: 'Custom websites and web applications built with modern technologies and optimized for performance.',
  },
  {
    icon: Bot,
    title: 'AI Product Engineering',
    description: 'Agentic workflows and intelligent automation with high-fidelity UX design for seamless user experiences.',
  },
];

const caseStudies = [
  {
    id: 'afil',
    title: 'Advanced Food Intolerance Labs',
    category: 'Health & Wellness',
    metric: '+44%',
    metricLabel: 'Conversion Rate',
    image: 'https://images.unsplash.com/photo-1535914254981-b5012eebbd15?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
    link: '/work/afil',
  },
  {
    id: 'mommas',
    title: "MoMMAs Voices",
    category: 'Nonprofit / Healthcare',
    metric: '37%',
    metricLabel: 'Email Open Rate',
    image: 'https://kajabi-storefronts-production.kajabi-cdn.com/kajabi-storefronts-production/file-uploads/themes/2153811776/settings_images/0f820ee-6e52-58bd-066b-5c6148844c4_MV_Website_Graphic_3_.png',
    link: '/work/mommas-voices',
  },
];

const testimonials = [
  {
    quote: "Kelsey is amazing! She kept up with all tasks asked and was open to feedback. She asked questions when needed. I allowed her creative control, and she did great with it.",
    author: "Client",
    role: "Online Business Owner",
    rating: 5,
  },
  {
    quote: "One of our core philosophies is that testing is essential to understanding what works. Partnering with Kelceymarie has helped us achieve this. She created and delivered five videos the same day we discussed ideas.",
    author: "Client",
    role: "Financial Services",
    rating: 5,
  },
  {
    quote: "Blown away with her work….. highly recommend.",
    author: "Client",
    role: "Recipe Content",
    rating: 5,
  },
  {
    quote: "Invested a good amount of time, much appreciated.",
    author: "Srikar K.",
    role: "Client",
    rating: 5,
  },
  {
    quote: "I loved the feedback Kelsey gave me, she audited several aspects of my tik tok account and competitors to define what I should be striving towards. On top of a profesional PDF case study. I would definelty recommend!",
    author: "Andre V.",
    role: "Client",
    rating: 5,
  },
  {
    quote: "One of our core philosophies is that testing is essential to understanding what works. Partnering with Kelceymarie has helped us achieve this. While other influencers took days to deliver, she created and delivered five videos the same day we discussed ideas. We will definitely work with her again.",
    author: "Labrynth Productions",
    role: "Client",
    rating: 5,
  },
  {
    quote: "Kelcey is professional, diligent and creative. Mostly, I can say she is nice. Working with someone who performs and is nice makes everything better.",
    author: "Phil B.",
    role: "Client",
    rating: 5,
  },
  {
    quote: "Kelcey did a great job and performed tasks exactly as they needed to be done. Thank you!",
    author: "Viktor B.",
    role: "Client",
    rating: 5,
  },
  {
    quote: "Kelsey is amazing! She kept up with all tasks asked and was open to feedback. She asked questions when needed. I allowed her creative control, and she did great with it.",
    author: "Amber D.",
    role: "Client",
    rating: 5,
  },
  {
    quote: "great quality work",
    author: "David. B.",
    role: "Client",
    rating: 5,
  },
  {
    quote: "Kelcey produced many amazing TikToks for us that generated a ton of views & brand recognition. One of our videos hit 800k views, plus we had a bunch of others that were 100k plus. Highly recommend",
    author: "Liam E.",
    role: "Client",
    rating: 5,
  },
  {
    quote: "Very creative and reliable! Love working with Kelcey",
    author: "Christie M",
    role: "Client",
    rating: 5,
  },
  {
    quote: "Michael did a great job with out website, we loved how communicative he was. We showed him inspo links from websites we liked and got exactly what we were looking for.",
    author: "Jon W.",
    role: "Client",
    rating: 5,
  },
];

const teamMembers = [
  {
    name: 'KM Warner, M. Ed.',
    role: 'Co-Founder & CEO',
    bio: "Kelceymarie Warner serves as Co-Founder and CEO, overseeing company strategy, client partnerships, and organizational growth. She leads high-level campaign planning, brand positioning, and performance strategy while ensuring every project meets measurable business objectives. Holding a Master's degree in Education, Kelceymarie applies research-based strategy, behavioral insight, and data analysis to drive results and guide decision-making. As the company's final decision-maker, she directs the vision, execution, and standards that define the firm's work.",
    image: '/images/team/km-warner.jpg',
  },
  {
    name: 'Jonathan Warner',
    role: 'Co-Founder & Chief Operating Officer',
    bio: "Jonathan Warner is Co-Founder and Chief Operating Officer, overseeing company operations, systems, and execution across all client projects. He ensures workflows run efficiently, deadlines are met, and every deliverable maintains the company's quality standards. Jonathan specializes in building streamlined processes, managing production timelines, and supporting team performance so client campaigns run smoothly from start to finish. His operational leadership allows the company to scale while maintaining consistency, efficiency, and results.",
    image: '/images/team/jonathan-warner.jpeg',
  },
  {
    name: 'Bee',
    role: 'Affiliate Marketing & Paid Media Manager',
    bio: 'Bee manages affiliate partnerships and paid advertising campaigns, overseeing strategy, targeting, optimization, and performance tracking. She focuses on maximizing return on investment, scaling high-performing campaigns, and identifying growth opportunities through data-driven decision making. Her work ensures clients reach the right audiences efficiently and convert traffic into measurable results.',
    image: 'https://ui-avatars.com/api/?name=Bee&background=23443d&color=f7efe5&size=512',
  },
  {
    name: 'Michael Cozart, MBA',
    role: 'Lead Software Architect & Technical Consultant',
    bio: 'Michael specializes in designing and implementing advanced web systems, infrastructure, and technical frameworks that power modern businesses. His work focuses on performance optimization, system reliability, and scalable architecture, ensuring clients have technology foundations built for long-term success.',
    image: '/images/team/michael-cozart.png',
  },
  {
    name: 'Athena Land',
    role: 'Operations Coordinator',
    bio: 'Athena supports daily workflows by managing schedules, organizing project timelines, preparing analytics reports, and keeping internal systems running smoothly. Their attention to detail and strong organizational skills help ensure projects stay on track and teams have the information they need to execute efficiently.',
    image: 'https://ui-avatars.com/api/?name=Athena+Land&background=23443d&color=f7efe5&size=512',
  },
];

const platforms = ['TikTok', 'Instagram', 'Facebook', 'LinkedIn'];

export const HomePage = () => {
  const [activeTestimonialIndex, setActiveTestimonialIndex] = useState(0);
  const activeTestimonial = testimonials[activeTestimonialIndex];

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setActiveTestimonialIndex((prev) => (prev + 1) % testimonials.length);
    }, 6000);

    return () => window.clearInterval(intervalId);
  }, []);

  return (
    <div className="min-h-screen bg-cream" data-testid="home-page">
      {/* Top Banner */}
      <div className="bg-forest text-cream py-3 text-center text-sm tracking-wide">
        <span className="opacity-80">DATA-DRIVEN DIGITAL MARKETING</span>
        <Link to="/contact" className="ml-2 text-gold-light font-medium hover:underline">
          GET STARTED →
        </Link>
      </div>

      {/* Hero Section */}
      <section className="min-h-[85vh] flex flex-col items-center justify-center relative px-6" data-testid="hero-section">
        {/* Watermark Background Text */}
        <div className="absolute inset-0 flex items-center justify-center overflow-hidden pointer-events-none select-none">
          <span className="text-[15vw] font-heading font-bold text-forest/[0.03] whitespace-nowrap">
            WARNER DIGITAL
          </span>
        </div>
        
        <motion.div
          className="text-center relative z-10 max-w-4xl"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1 className="text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-heading font-bold text-forest leading-[1.1] mb-6">
            Unlock Your Digital Potential.
          </h1>
          <p className="text-lg md:text-xl text-forest/70 mt-8 max-w-xl mx-auto leading-relaxed">
            Where Market Research Meets Measurable Results.
          </p>
          
          {/* Scroll Indicator */}
          <motion.div 
            className="mt-12"
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <div className="w-10 h-10 rounded-full bg-gold flex items-center justify-center mx-auto cursor-pointer hover:bg-gold-light transition-colors">
              <ArrowDown className="text-forest-dark" size={20} />
            </div>
          </motion.div>
        </motion.div>
      </section>

      {/* Platforms Bar */}
      <section className="py-8 border-y border-forest/10 overflow-hidden bg-cream-paper">
        <div className="flex items-center justify-center gap-12 flex-wrap px-6">
          {platforms.map((platform) => (
            <span 
              key={platform} 
              className="text-forest/40 font-heading font-semibold text-lg tracking-wider uppercase"
            >
              {platform}
            </span>
          ))}
        </div>
      </section>

      {/* Strategy Section */}
      <section className="section-padding bg-cream" data-testid="strategy-section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-forest leading-[1.1] mb-6">
                A data-driven team that becomes your trusted partner.
              </h2>
              
              <p className="text-gold font-semibold tracking-widest uppercase text-sm mt-8 mb-4">
                Industries Served
              </p>
              <p className="text-forest/70 leading-relaxed mb-4">
                We support B2B and B2C brands in reaching more potential clients locally, 
                regionally and nationally in these industries:
              </p>
              <p className="text-forest font-medium">
                Health & Wellness, Nonprofits, E-commerce, Professional Services, SaaS, Finance.
              </p>
              
              <Link to="/contact" className="mt-8 inline-block" data-testid="strategy-cta">
                <Button className="bg-gold text-forest-dark hover:bg-gold-light rounded-full px-8 py-6 text-base font-semibold tracking-wide uppercase">
                  Contact Us
                  <ArrowRight className="ml-2" size={18} />
                </Button>
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <img 
                src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?crop=entropy&cs=srgb&fm=jpg&q=85&w=800" 
                alt="Team collaboration"
                className="rounded-lg grayscale hover:grayscale-0 transition-all duration-500 w-full"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Results Statement */}
      <section className="section-padding bg-forest" data-testid="results-section">
        <div className="container-custom">
          <motion.div
            className="max-w-4xl mx-auto text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-cream leading-[1.2]">
              We have helped brands earn{' '}
              <span className="text-gold">millions of impressions</span>{' '}
              and drive{' '}
              <span className="text-gold">measurable growth</span>{' '}
              over the past years.
            </h2>
            <p className="text-cream/70 mt-8 text-lg max-w-2xl mx-auto">
              Our services include: strategic planning, content creation, affiliate marketing, 
              web development, AI product engineering, and done-for-you social media management 
              that positions your business for rapid growth.
            </p>
            <motion.div 
              className="mt-10"
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <div className="w-10 h-10 rounded-full bg-gold flex items-center justify-center mx-auto">
                <ArrowDown className="text-forest-dark" size={20} />
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Team Section */}
      <section className="section-padding bg-cream-paper" data-testid="team-section">
        <div className="container-custom">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-gold font-semibold tracking-widest uppercase text-sm mb-4">
              Our Team
            </p>
            <h2 className="text-4xl md:text-5xl font-heading font-bold text-forest">
              Meet the Experts
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-12">
            {teamMembers.map((member, index) => (
              <motion.div
                key={member.name}
                className="text-center"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.15 }}
                data-testid={`team-member-${index}`}
              >
                {member.image && (
                  <div className="relative mb-6 mx-auto w-64 h-80 overflow-hidden rounded-lg">
                    <img 
                      src={member.image} 
                      alt={member.name}
                      loading="lazy"
                      onError={(event) => {
                        event.currentTarget.onerror = null;
                        event.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(member.name)}&background=23443d&color=f7efe5&size=512`;
                      }}
                      className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-500"
                    />
                  </div>
                )}
                <h3 className="text-2xl font-heading font-bold text-forest mb-2">
                  {member.name}
                </h3>
                <p className="text-forest/60 italic">
                  {member.role}
                </p>
                <p className="text-forest/70 mt-4 leading-relaxed max-w-2xl mx-auto">
                  {member.bio}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="section-padding bg-cream" data-testid="services-section">
        <div className="container-custom">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-gold font-semibold tracking-widest uppercase text-sm mb-4">
              What We Do
            </p>
            <h2 className="text-4xl md:text-5xl font-heading font-bold text-forest">
              Our Services
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                className="group p-6 border-b-2 border-forest/10 hover:border-gold transition-colors"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                data-testid={`service-card-${index}`}
              >
                <div className="w-12 h-12 bg-gold/20 rounded-full flex items-center justify-center mb-4 group-hover:bg-gold transition-colors">
                  <service.icon className="text-forest group-hover:text-forest-dark transition-colors" size={22} />
                </div>
                <h3 className="text-lg font-heading font-semibold text-forest mb-2">{service.title}</h3>
                <p className="text-forest/60 text-sm leading-relaxed">{service.description}</p>
              </motion.div>
            ))}
          </div>

          <motion.div 
            className="text-center mt-12"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <Link to="/services" data-testid="services-cta">
              <Button variant="outline" className="border-2 border-forest text-forest hover:bg-forest hover:text-cream rounded-full px-8 py-6 text-base tracking-wide uppercase">
                Explore All Services
                <ArrowRight className="ml-2" size={18} />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="section-padding bg-cream-paper" data-testid="testimonials-section">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <motion.div
              key={activeTestimonialIndex}
              className="text-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35 }}
              data-testid={`testimonial-${activeTestimonialIndex}`}
            >
              <div className="flex justify-center gap-1 mb-6">
                {[...Array(activeTestimonial.rating)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 fill-gold text-gold" />
                ))}
              </div>
              <blockquote className="text-2xl md:text-3xl font-heading text-forest leading-relaxed italic mb-8">
                "{activeTestimonial.quote}"
              </blockquote>
              <div>
                <p className="font-semibold text-forest">{activeTestimonial.author}</p>
                {activeTestimonial.role && (
                  <p className="text-forest/60 text-sm">{activeTestimonial.role}</p>
                )}
              </div>
            </motion.div>

            <div className="flex justify-center gap-2 mt-8" role="tablist" aria-label="Testimonials">
              {testimonials.map((testimonial, index) => (
                <button
                  key={`${testimonial.author}-${index}`}
                  type="button"
                  onClick={() => setActiveTestimonialIndex(index)}
                  className={`h-2.5 rounded-full transition-all ${
                    activeTestimonialIndex === index ? 'w-8 bg-gold' : 'w-2.5 bg-forest/20 hover:bg-forest/40'
                  }`}
                  aria-label={`Show testimonial ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Case Studies Preview */}
      <section className="section-padding bg-cream" data-testid="case-studies-section">
        <div className="container-custom">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-gold font-semibold tracking-widest uppercase text-sm mb-4">
              Our Work
            </p>
            <h2 className="text-4xl md:text-5xl font-heading font-bold text-forest">
              Case Studies
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {caseStudies.map((study, index) => (
              <motion.div
                key={study.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
              >
                <Link 
                  to={study.link} 
                  className="group block"
                  data-testid={`case-study-${study.id}`}
                >
                  <div className="relative overflow-hidden rounded-lg aspect-[4/3] mb-6">
                    <img 
                      src={study.image} 
                      alt={study.title}
                      className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500 group-hover:scale-105"
                    />
                  </div>
                  <p className="text-gold font-semibold tracking-widest uppercase text-xs mb-2">
                    {study.category}
                  </p>
                  <h3 className="text-2xl font-heading font-bold text-forest mb-2 group-hover:text-gold transition-colors">
                    {study.title}
                  </h3>
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl font-bold text-gold">{study.metric}</span>
                    <span className="text-forest/60">{study.metricLabel}</span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>

          <motion.div 
            className="text-center mt-12"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <Link to="/work" data-testid="work-cta">
              <Button className="bg-gold text-forest-dark hover:bg-gold-light rounded-full px-8 py-6 text-base font-semibold tracking-wide uppercase">
                View All Case Studies
                <ArrowRight className="ml-2" size={18} />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="section-padding bg-forest" data-testid="cta-section">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-cream mb-6">
              Ready to grow your brand?
            </h2>
            <Link to="/contact" data-testid="footer-cta-primary">
              <Button className="bg-gold text-forest-dark hover:bg-gold-light rounded-full px-10 py-6 text-lg font-semibold tracking-wide uppercase">
                Schedule a Call
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
